import { useApolloClient, useLazyQuery } from '@apollo/client'
import DownloadIcon from '@mui/icons-material/CloudDownloadOutlined'
import { Tooltip } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import { isEmpty } from 'lodash'
import { v4 } from 'uuid'
import { ImageDocument, MediaFragmentFragmentDoc } from '../../../../../../graphql/generated'
import { alertsVar } from '../../../../../../providers/apollo/cache'
import { Alert } from '../../../../../../providers/apollo/models/alert'
import logger from '../../../../../../utils/logger'
import useImageDownload from '../../hooks/useImageDownload'

interface Props {
  color?: 'inherit' | 'primary' | 'secondary'
  ids: string[]
  onCompleted?: () => void
  isUnauthenticated?: boolean
  isPublic?: boolean
}

/**
 * The useImageDownload hook returns two executable properties.
 *
 * However the downloadMultiple only requires media ids while the single download
 * requires the mdeia url and title. This creates a problem because the ids map
 * only stores media ids as keys.
 *
 * Therefore, we can either populate the ids with the full image object which takes
 * addtional space or update the hook only accept an id/ids as an arguement and perform a query within the hook
 * if the url is needed.
 */

export default function MediaDownload({
  ids,
  color = 'inherit',
  onCompleted = undefined,
  isUnauthenticated = false,
  isPublic = false
}: Readonly<Props>) {
  const client = useApolloClient()
  const { download, downloadMultiple } = useImageDownload()
  const isDisabled = isEmpty(ids) || (isUnauthenticated && ids.length > 1)
  const [execute] = useLazyQuery(ImageDocument, {
    onError: () => {
      const alert: Alert = {
        id: v4(),
        message: 'There was a problem downloading your image. Please try again later.',
        severity: 'error'
      }
      alertsVar([alert])
    },
    onCompleted: ({ image }) => {
      if (!image) return
      const {
        id,
        originalFilename,
        title,
        image: { url }
      } = image
      download({ title, originalFilename, url, id })
    }
  })

  const handleClick = () => {
    if (ids.length === 1) {
      if (isPublic) {
        const media = client.readFragment({ id: `Image:${ids[0]}`, fragment: MediaFragmentFragmentDoc })
        const url = media?.image.url ?? ''
        const title = media?.title ?? ids[0]
        const originalFilename = media?.originalFilename
        if (!originalFilename) {
          alertsVar([{ id: v4(), message: 'There was an error downloading your image', severity: 'error' }])
          logger.error(`Unable to find originalFilename for ${media}`)
          return
        }
        download({ title, originalFilename, url, id: ids[0], isPublic })
      } else {
        // TODO: Move query to useImageDownload hook
        const variables = { id: ids[0] }
        execute({ variables })
      }
    } else {
      downloadMultiple({ imageIds: ids })
    }

    if (onCompleted) {
      onCompleted()
    }
  }

  return (
    <IconButton aria-label="Download Media" color={color} disabled={isDisabled} onClick={handleClick} data-test="download" id="download-button">
      <Tooltip title="Download" arrow>
        <DownloadIcon />
      </Tooltip>
    </IconButton>
  )
}
